import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCopyLanguage, useMe, usePublicLanguages } from '../api/hooks'
import type { Language } from '../api/types'
import { Badge, Button, Card, EmptyState, ErrorText, Input, Spinner } from '../components/ui'

export default function Browse() {
  const [search, setSearch] = useState('')
  const { data: languages, isPending, error } = usePublicLanguages(search)
  const { data: user } = useMe()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Browse languages</h1>
        <p className="text-sm text-muted">
          Languages other people have published. Copy one to get your own editable
          version and start learning it.
        </p>
      </div>

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search published languages…"
        className="max-w-sm"
      />

      <ErrorText error={error} />

      {isPending ? (
        <Spinner />
      ) : languages && languages.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2">
          {languages.map((language) => (
            <li key={language.id}>
              <PublicLanguageCard language={language} signedIn={Boolean(user)} />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          title={search ? 'No matches' : 'Nothing published yet'}
          hint={
            search
              ? 'Try a different search.'
              : 'Be the first — publish one of your own languages from its page.'
          }
        />
      )}
    </div>
  )
}

function PublicLanguageCard({
  language,
  signedIn,
}: {
  language: Language
  signedIn: boolean
}) {
  const copy = useCopyLanguage()
  const navigate = useNavigate()

  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-medium text-ink">{language.name}</h2>
        {language.is_owner && <Badge>Yours</Badge>}
      </div>

      {language.description && (
        <p className="mt-1 line-clamp-3 text-sm text-muted">{language.description}</p>
      )}

      <p className="mt-3 text-sm text-muted">
        {language.entry_count} {language.entry_count === 1 ? 'word' : 'words'}
        {language.owner_name && ` · by ${language.owner_name}`}
      </p>

      <div className="mt-4 flex items-center gap-2 pt-1">
        <Button
          onClick={() => {
            if (!signedIn) {
              navigate('/login', { state: { from: '/browse' } })
              return
            }
            // Land the user in their new copy rather than leaving them on the
            // browse page wondering whether anything happened.
            copy.mutate(language.id, {
              onSuccess: (created) => navigate(`/languages/${created.id}`),
            })
          }}
          disabled={copy.isPending}
        >
          {copy.isPending ? 'Copying…' : signedIn ? 'Copy & learn' : 'Sign in to copy'}
        </Button>
      </div>

      <ErrorText error={copy.error} />
    </Card>
  )
}
